<template>
  <div class="w-full overflow-y-auto rounded-lg bg-eblack-100/70">
    <form
      @submit.prevent="action()"
      class="flex flex-col justify-center p-2 px-4 border-gray-200 rounded-lg gap-y-4"
    >
      <div v-if="errors.length" ref="errorSummary" role="alert" tabindex="-1"
        class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
        <p v-if="errorFields.length" class="font-medium">{{ $t("provider.validation.review_fields") }}</p>
        <ul v-if="errorFields.length" class="mt-1 flex flex-wrap gap-x-4 gap-y-1">
          <li v-for="field in errorFields" :key="field">
            <a :href="`#provider-${field}`" @click.prevent="focusField(field)"
              class="underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2">
              {{ fieldLabel(field) }}
            </a>
          </li>
        </ul>
        <p v-if="fieldError('form')">{{ fieldError('form') }}</p>
      </div>
      <div class="flex flex-col gap-y-2">
        <label for="provider-name" class="text-sm md:text-md">{{ $t("provider.auto.nombre") }}</label>
        <input
          type="text"
          name="name"
          id="provider-name"
          :aria-invalid="Boolean(fieldError('name'))"
          :aria-describedby="fieldError('name') ? 'provider-name-error' : undefined"
          @input="clearFieldError('name')"
          class="text-sm border border-gray-300 rounded-lg bg-gray-50 md:text-md right-1 focus:ring-blue-400"
          :placeholder="$t('provider.auto.john_doe')"
          v-bind:class="{
            'border-red-500 focus:border-red-500 focus:ring-red-500': fieldError('name'),
          }"
          v-model="provider_form.name"
          :disabled="showDetails"
        />
        <p
          class="pl-2 text-xs text-red-700 md:text-sm"
          v-if="fieldError('name')"
          id="provider-name-error"
        >
          {{ fieldError('name') }}
        </p>
      </div>

      <div class="flex flex-col gap-y-2">
        <label for="provider-primary_contact" class="text-sm md:text-md"
          >{{ $t("provider.auto.telefono_principal") }}</label
        >
        <PhoneInput
          v-model="provider_form.primary_contact"
          id="provider-primary_contact"
          name="primary_contact"
          :aria-describedby="fieldError('primary_contact') ? 'provider-primary_contact-error' : undefined"
          @update:model-value="clearFieldError('primary_contact')"
          required
          :default-country="provider_form.country || 'CR'"
          :invalid="
            Boolean(fieldError('primary_contact'))
          "
          :disabled="showDetails"
        />
        <p
          class="pl-2 text-xs text-red-700 md:text-sm"
          v-if="fieldError('primary_contact')"
          id="provider-primary_contact-error"
        >
          {{ fieldError('primary_contact') }}
        </p>
      </div>

      <div class="flex flex-col gap-y-2">
        <label for="provider-second_contact" class="text-sm md:text-md"
          >{{ $t("provider.auto.telefono_secundario") }}</label
        >
        <PhoneInput
          v-model="provider_form.second_contact"
          id="provider-second_contact"
          name="second_contact"
          :aria-describedby="fieldError('second_contact') ? 'provider-second_contact-error' : undefined"
          @update:model-value="clearFieldError('second_contact')"
          :invalid="Boolean(fieldError('second_contact'))"
          :default-country="provider_form.country || 'CR'"
          :disabled="showDetails"
        />
        <p v-if="fieldError('second_contact')" id="provider-second_contact-error" class="pl-2 text-xs text-red-700 md:text-sm">
          {{ fieldError('second_contact') }}
        </p>
      </div>

      <div class="flex flex-col gap-y-2">
        <label for="provider-email" class="text-sm md:text-md">{{ $t("provider.auto.correo") }}</label>
        <input
          type="text"
          name="email"
          id="provider-email"
          :aria-invalid="Boolean(fieldError('email'))"
          :aria-describedby="fieldError('email') ? 'provider-email-error' : undefined"
          @input="clearFieldError('email')"
          class="text-sm border border-gray-300 rounded-lg bg-gray-50 md:text-md right-1 focus:ring-blue-400"
          placeholder="contact@example.com"
          v-bind:class="{ 'border-red-500 focus:border-red-500 focus:ring-red-500': fieldError('email') }"
          v-model="provider_form.email"
          :disabled="showDetails"
        />
        <p
          class="pl-2 text-xs text-red-700 md:text-sm"
          v-if="fieldError('email')"
          id="provider-email-error"
        >
          {{ fieldError('email') }}
        </p>
      </div>

      <div class="flex flex-col gap-y-2">
        <label for="provider-dni" class="text-sm md:text-md">{{ $t("provider.auto.identificacion") }}</label>
        <input
          type="text"
          name="dni"
          id="provider-dni"
          :aria-invalid="Boolean(fieldError('dni'))"
          :aria-describedby="fieldError('dni') ? 'provider-dni-error' : undefined"
          @input="clearFieldError('dni')"
          class="text-sm border border-gray-300 rounded-lg bg-gray-50 md:text-md right-1 focus:ring-blue-400"
          placeholder="1-2345-6789"
          v-bind:class="{
            'border-red-500 focus:border-red-500 focus:ring-red-500': fieldError('dni'),
          }"
          v-model="provider_form.dni"
          :disabled="showDetails"
        />
        <p
          class="pl-2 text-xs text-red-700 md:text-sm"
          v-if="fieldError('dni')"
          id="provider-dni-error"
        >
          {{ fieldError('dni') }}
        </p>
      </div>

      <div v-if="!privateContext" class="flex flex-col gap-y-2">
        <label for="provider-commission_amount" class="text-sm md:text-md"
          >{{ $t("provider.auto.comision") }}</label
        >
        <div class="flex items-center gap-2">
          <input
            type="number"
            name="commission_amount"
            id="provider-commission_amount"
            :aria-invalid="Boolean(fieldError('commission_amount'))"
            :aria-describedby="fieldError('commission_amount') ? 'provider-commission_amount-error' : undefined"
            @input="clearFieldError('commission_amount')"
            min="0"
            step="0.01"
            class="flex-1 text-sm border border-gray-300 rounded-lg bg-gray-50 md:text-md focus:ring-blue-400"
            placeholder="0.00"
            v-bind:class="{
              'border-red-500 focus:border-red-500 focus:ring-red-500':
                fieldError('commission_amount'),
            }"
            v-model.number="provider_form.commission_amount"
            :disabled="showDetails"
          />
          <div
            class="flex border border-gray-300 rounded-lg bg-eblack-100 h-[38px]"
          >
            <button
              type="button"
              :class="[
                'px-3 py-1 text-md focus:outline-none transition-colors rounded-l-lg',
                provider_form.commission_type === 'PERCENTAGE'
                  ? 'bg-white shadow'
                  : 'bg-transparent text-black',
              ]"
              @click="setCommissionType('PERCENTAGE')"
              :disabled="showDetails"
            >
              %
            </button>
            <button
              type="button"
              :class="[
                'px-3 py-1 text-md focus:outline-none transition-colors rounded-r-lg',
                provider_form.commission_type === 'FIXED'
                  ? 'bg-white shadow'
                  : 'bg-transparent text-black',
              ]"
              @click="setCommissionType('FIXED')"
              :disabled="showDetails"
            >
              #
            </button>
          </div>
        </div>
        <p
          class="pl-2 text-xs text-red-700 md:text-sm"
          v-if="fieldError('commission_amount')"
          id="provider-commission_amount-error"
        >
          {{ fieldError('commission_amount') }}
        </p>
      </div>

      <div class="flex flex-col gap-y-2">
        <label for="provider-country" class="text-sm md:text-md">{{ $t("provider.auto.pais") }}</label>
        <select
          name="country"
          id="provider-country"
          :aria-invalid="Boolean(fieldError('country'))"
          :aria-describedby="fieldError('country') ? 'provider-country-error' : undefined"
          @change="clearFieldError('country')"
          class="text-sm border border-gray-300 rounded-lg bg-gray-50 md:text-md right-1 focus:ring-blue-400"
          v-bind:class="{ 'border-red-500 focus:border-red-500 focus:ring-red-500': fieldError('country') }"
          v-model="provider_form.country"
          :disabled="showDetails"
        >
          <option value="">{{ $t("provider.auto.seleccione_el_pais") }}</option>
          <option
            v-for="country in countries"
            v-bind:key="country.id"
            :value="country.iso2"
          >
            {{ country.name }}
          </option>
        </select>
        <p
          class="pl-2 text-xs text-red-700 md:text-sm"
          v-if="fieldError('country')"
          id="provider-country-error"
        >
          {{ fieldError('country') }}
        </p>
      </div>

      <div class="flex flex-col gap-y-4" v-if="provider_form.country == 'CR'">
        <div class="flex flex-col gap-y-2" v-if="location_one.length > 0">
          <label for="provider-location_one" class="text-sm md:text-md">{{ $t("provider.auto.provincia") }}</label>
          <select
            name="location_one"
            id="provider-location_one"
            :aria-invalid="Boolean(fieldError('location_one'))"
            :aria-describedby="fieldError('location_one') ? 'provider-location_one-error' : undefined"
            @change="clearFieldError('location_one')"
            class="text-sm border border-gray-300 rounded-lg bg-gray-50 md:text-md right-1 focus:ring-blue-400"
            v-bind:class="{ 'border-red-500 focus:border-red-500 focus:ring-red-500': fieldError('location_one') }"
            v-model="provider_form.location_one"
            :disabled="showDetails"
          >
            <option
              v-for="state in location_one"
              v-bind:key="state.id"
              :value="state.name.toLowerCase()"
            >
              {{ state.name }}
            </option>
          </select>
          <p
            class="pl-2 text-xs text-red-700 md:text-sm"
            v-if="fieldError('location_one')"
            id="provider-location_one-error"
          >
            {{ fieldError('location_one') }}
          </p>
        </div>
        <div class="flex flex-col gap-y-2" v-if="location_two.length > 0">
          <label for="provider-location_two" class="text-sm md:text-md">{{ $t("provider.auto.canton") }}</label>
          <select
            name="location_two"
            id="provider-location_two"
            :aria-invalid="Boolean(fieldError('location_two'))"
            :aria-describedby="fieldError('location_two') ? 'provider-location_two-error' : undefined"
            @change="clearFieldError('location_two')"
            class="text-sm border border-gray-300 rounded-lg bg-gray-50 md:text-md right-1 focus:ring-blue-400"
            v-bind:class="{ 'border-red-500 focus:border-red-500 focus:ring-red-500': fieldError('location_two') }"
            v-model="provider_form.location_two"
            :disabled="showDetails"
          >
            <option
              v-for="city in location_two"
              v-bind:key="city.id"
              :value="city.name.toLowerCase()"
            >
              {{ city.name }}
            </option>
          </select>
          <p
            class="pl-2 text-xs text-red-700 md:text-sm"
            v-if="fieldError('location_two')"
            id="provider-location_two-error"
          >
            {{ fieldError('location_two') }}
          </p>
        </div>
      </div>

      <div class="flex flex-col gap-y-4" v-if="provider_form.country != 'CR'">
        <div class="flex flex-col gap-y-2" v-if="location_one.length > 0">
          <label for="provider-location_one" class="text-sm md:text-md">{{ $t("provider.auto.estado") }}</label>
          <select
            name="location_one"
            id="provider-location_one"
            :aria-invalid="Boolean(fieldError('location_one'))"
            :aria-describedby="fieldError('location_one') ? 'provider-location_one-error' : undefined"
            @change="clearFieldError('location_one')"
            class="text-sm border border-gray-300 rounded-lg bg-gray-50 md:text-md right-1 focus:ring-blue-400"
            v-bind:class="{ 'border-red-500 focus:border-red-500 focus:ring-red-500': fieldError('location_one') }"
            v-model="provider_form.location_one"
            :disabled="showDetails"
          >
            <option
              v-for="state in location_one"
              v-bind:key="state.id"
              :value="state.name.toLowerCase()"
            >
              {{ state.name }}
            </option>
          </select>
          <p
            class="pl-2 text-xs text-red-700 md:text-sm"
            v-if="fieldError('location_one')"
            id="provider-location_one-error"
          >
            {{ fieldError('location_one') }}
          </p>
        </div>
        <div class="flex flex-col gap-y-2" v-if="location_two.length > 0">
          <label for="provider-location_two" class="text-sm md:text-md">{{ $t("provider.auto.ciudad") }}</label>
          <select
            name="location_two"
            id="provider-location_two"
            :aria-invalid="Boolean(fieldError('location_two'))"
            :aria-describedby="fieldError('location_two') ? 'provider-location_two-error' : undefined"
            @change="clearFieldError('location_two')"
            class="text-sm border border-gray-300 rounded-lg bg-gray-50 md:text-md right-1 focus:ring-blue-400"
            v-bind:class="{ 'border-red-500 focus:border-red-500 focus:ring-red-500': fieldError('location_two') }"
            v-model="provider_form.location_two"
            :disabled="showDetails"
          >
            <option
              v-for="city in location_two"
              v-bind:key="city.id"
              :value="city.name.toLowerCase()"
            >
              {{ city.name }}
            </option>
          </select>
          <p
            class="pl-2 text-xs text-red-700 md:text-sm"
            v-if="fieldError('location_two')"
            id="provider-location_two-error"
          >
            {{ fieldError('location_two') }}
          </p>
        </div>
      </div>

      <div class="flex flex-col gap-y-2">
        <label for="provider-description" class="text-sm">{{ $t("provider.auto.descripcion") }}</label>
        <textarea
          id="provider-description"
          :aria-invalid="Boolean(fieldError('description'))"
          :aria-describedby="fieldError('description') ? 'provider-description-error' : undefined"
          @input="clearFieldError('description')"
          rows="4"
          class="block p-2.5 w-full text-sm bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
          :placeholder="$t('provider.auto.escriba_de_que_trata_el_tour')"
          v-bind:class="{
            'border-red-500 focus:border-red-500 focus:ring-red-500':
              fieldError('description'),
          }"
          v-model="provider_form.description"
          :disabled="showDetails"
        ></textarea>

        <p
          class="pl-2 text-xs text-red-700 md:text-sm"
          v-if="fieldError('description')"
          id="provider-description-error"
        >
          {{ fieldError('description') }}
        </p>
      </div>

      <div class="flex" v-if="!showDetails">
        <button
          type="submit"
          class="px-4 py-1.5 rounded-lg bg-green-500 text-white"
          :class="[loading ? 'animate animate-bouncing' : '']"
        >
          {{ button_action }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { i18n } from "@/i18n";
import { isPrivateProviderContext, providerFieldLabels, providerFormErrors } from '@/services/admin/private-provider-management';
import providesRequests from "@/services/admin/provider-service";
import data from "@/core/data";
import { useToast } from "vue-toastification";
import PhoneInput from "@/components/ui/PhoneInput.vue";
export default {
  props: [
    "provider",
    "closeModal",
    "handleRequestInitData",
    "showDetails",
    "overlayService",
  ],
  name: "FormTab",
  components: {
    PhoneInput,
  },
  data() {
    return {
      button_action: "Guardar",
      provider_form: {
        name: "",
        primary_contact: "",
        second_contact: "",
        email: "",
        dni: "",
        commission_amount: 0,
        commission_type: "percentage",
        location_one: "",
        location_two: "",
        country: "",
        country_name: "",
        state: "active",
        description: "",
      },
      provider_id: 0,
      errors: [],
      country_selected: null,
      countries: [],
      location_one: [],
      location_two: [],
      location_three: [],
      loading: false,
      toast: useToast(),
    };
  },
  watch: {
    "provider_form.country": {
      handler(value) {
        this.loadStates(value);
      },
      immediate: true,
    },
    "provider_form.location_one": {
      handler(value) {
        this.loadCities(value);
      },
      immediate: true,
    },
    provider: {
      handler(value) {
        if (value) {
          let temp_data = { ...value };
          delete temp_data.services;
          delete temp_data.category;
          delete temp_data.created_at;
          delete temp_data.updated_at;
          delete temp_data.deleted_at;
          this.provider_id = value.id;
          this.provider_form = temp_data;
          delete this.provider_form.id;
          this.button_action = "Actualizar";
        } else {
          this.resetForm();
        }
      },
      immediate: true,
    },
  },
  computed: {
    privateContext() { return isPrivateProviderContext(); },
    errorFields() { return this.errors.flatMap((error) => Object.keys(error)).filter((field) => field !== 'form'); },
  },
  methods: {
    action() {
      this.errors = [];
      this.loading = true;

      if (this.provider) {
        this.update();
      } else {
        this.save();
      }
    },
    update() {
      this.button_action = "Actualizando...";

      providesRequests
        .updateProvider(this.provider_id, this.provider_form)
        .then((res) => {
          if (res.status === 200 || res.status === 201) {
            this.button_action = "Actualizar";
            this.resetForm();
            this.handleRequestInitData();
            if (this.overlayService) {
              this.overlayService.closeTop(true);
            } else {
              this.closeModal();
            }
            this.toast.success(i18n.global.t("provider.auto.proveedor_actualizado"), {
              timeout: 3000,
              position: "top-right",
            });
            this.loading = false;
          }
        })
        .catch((error) => {
          this.handleSaveError(error, "Actualizar");
        });
    },
    save() {
      this.button_action = "Guardando...";
      providesRequests
        .saveProvider(this.provider_form)
        .then((res) => {
          if (res.status === 200 || res.status === 201) {
            this.button_action = "Guardar";
            this.resetForm();
            this.handleRequestInitData();
            if (this.overlayService) {
              this.overlayService.closeTop(true);
            } else {
              this.closeModal();
            }
            this.toast.success(i18n.global.t("provider.auto.proveedor_guardado"), {
              timeout: 3000,
              position: "top-right",
            });
          }
          this.loading = false;
        })
        .catch((error) => {
          this.handleSaveError(error, "Guardar");
        });
    },
    fieldError(field) {
      return this.errors.find((error) => error[field])?.[field];
    },
    fieldLabel(field) {
      if (field === 'location_one' && this.provider_form.country !== 'CR') return this.$t('provider.auto.estado');
      if (field === 'location_two' && this.provider_form.country !== 'CR') return this.$t('provider.auto.ciudad');
      return this.$t(providerFieldLabels[field]);
    },
    clearFieldError(field) {
      this.errors = this.errors.filter((error) => !Object.hasOwn(error, field));
    },
    focusField(field) {
      this.$el.querySelector(`#provider-${field}`)?.focus();
    },
    handleSaveError(error, action) {
      this.errors = providerFormErrors(error, i18n.global.t("provider.validation.save_error"), (key) => i18n.global.t(key));
      this.button_action = action;
      this.loading = false;
      this.$nextTick(() => {
        if (this.errorFields.length) this.focusField(this.errorFields[0]);
        else this.$refs.errorSummary?.focus();
      });
    },
    loadStates(value) {
      let state = this.countries.filter((item) => item.iso2 == value);
      if (state.length > 0) {
        this.country_selected = state[0];
        this.provider_form.country_name = state[0].name;
        this.location_one = state[0].states;
      }
    },
    loadCities(value) {
      if (this.country_selected) {
        let cities = this.country_selected.states.filter(
          (item) => item.name.toLowerCase() == value
        );

        if (cities.length > 0) {
          this.location_two = cities[0].cities;
        }
      }
    },
    resetForm() {
      this.provider_form = {
        name: "",
        primary_contact: "",
        second_contact: "",
        email: "",
        dni: "",
        commission_amount: 0,
        commission_type: "percentage",
        location_one: "",
        location_two: "",
        location_three: "",
        country: "",
        country_name: "",
        state: "active",
        description: "",
      };
      this.button_action = "Guardar";
    },
    setCommissionType(type) {
      this.provider_form.commission_type = type;
    },
  },
  mounted() {
    this.countries = data.countriesStatesCities;
  },
};
</script>

<style lang="scss" scoped></style>
